import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, Flame, Highlighter, Feather, IceCream, ArrowRight, BellRing, BellOff, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  isPushSupported, getPushPreferences, enablePush, disablePush, sendTestPush,
} from '../utils/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'lumera.dailyMoment.dismissed';
const NOTIFY_OPT_IN_KEY = 'lumera.dailyMoment.notify';

const ICON_MAP = {
  flame: Flame,
  highlighter: Highlighter,
  feather: Feather,
  'ice-cream': IceCream,
};

/**
 * Maps a `daily-moment` `primary_action` (and `secondary_actions[i]`) onto
 * an internal SPA URL. Mirrors `urlFor` in CompanionActions.jsx but kept
 * locally so the moment card can navigate without coupling to that module.
 */
const urlForAction = (a) => {
  if (!a) return null;
  const p = a.params || {};
  const sp = new URLSearchParams();
  switch (a.type) {
    case 'open_verse':
      if (p.book) sp.set('book', p.book);
      if (p.chapter != null) sp.set('chapter', String(p.chapter));
      if (p.verse != null) sp.set('verse', String(p.verse));
      return `/bible${sp.toString() ? `?${sp.toString()}` : ''}`;
    case 'mh_commentary':
      if (p.book) sp.set('book', p.book);
      if (p.chapter != null) sp.set('chapter', String(p.chapter));
      sp.set('mh', '1');
      return `/bible?${sp.toString()}`;
    case 'sermon_prep':
      if (p.topic) sp.set('topic', p.topic);
      return `/sermon-prep${sp.toString() ? `?${sp.toString()}` : ''}`;
    case 'preach_mode':
      if (p.draft_id) sp.set('draft_id', p.draft_id);
      return `/sermon-prep/present${sp.toString() ? `?${sp.toString()}` : ''}`;
    case 'play_game':
      sp.set('tab', 'games');
      if (p.game === 'sundae') sp.set('sundae', 'start');
      else if (p.game) sp.set('game', p.game);
      return `/community?${sp.toString()}`;
    case 'view_highlights':
      return '/highlights';
    case 'word_study':
      if (p.word) sp.set('word', p.word);
      if (p.strongs) sp.set('strongs', p.strongs);
      return `/word-study${sp.toString() ? `?${sp.toString()}` : ''}`;
    default:
      return null;
  }
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const HOUR_OPTIONS = [6, 7, 8, 9, 12, 17, 19, 21];
const formatHour = (h) => {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

/**
 * Daily Lumera Moment — a single dismissible, personalized pull-back card
 * pinned at the top of the dashboard. Selection rules live on the backend
 * (`get_daily_moment`); the card just renders + navigates.
 *
 * Notification opt-in (server-side VAPID Web Push):
 *   1. User taps bell → permission + Service Worker subscribe.
 *   2. We POST the subscription + preferred local hour to `/api/push/subscribe`.
 *   3. Backend scheduler (APScheduler, every 15 min) sends a real OS-level
 *      push at that hour each day, even if the tab is closed.
 *
 * Falls back to a one-shot in-tab Notification if the browser doesn't support
 * push (private mode, older Safari, etc.).
 *
 * Dismissal is sticky for one calendar day: clicking the X writes today's
 * date into localStorage and the card disappears until tomorrow.
 */
const DailyMomentCard = ({ language = 'en' }) => {
  const navigate = useNavigate();
  const [moment, setMoment] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [notifyOptIn, setNotifyOptIn] = useState(false);
  const [hourLocal, setHourLocal] = useState(8);
  const [showHourPicker, setShowHourPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hide if dismissed today; load current push preference from server.
  useEffect(() => {
    try {
      const dismissedAt = window.localStorage.getItem(STORAGE_KEY);
      if (dismissedAt === todayKey()) setHidden(true);
    } catch {
      /* private mode — proceed */
    }
    let cancelled = false;
    (async () => {
      const prefs = await getPushPreferences();
      if (cancelled) return;
      setNotifyOptIn(!!prefs.enabled);
      const first = prefs.subscriptions?.[0];
      if (first && Number.isInteger(first.hour_local)) setHourLocal(first.hour_local);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (hidden) return undefined;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/lumera/daily-moment`, { params: { lang: language }, withCredentials: true })
      .then((r) => { if (!cancelled) setMoment(r.data); })
      .catch(() => { if (!cancelled) setMoment(null); });
    return () => { cancelled = true; };
  }, [hidden, language]);

  if (hidden || !moment) return null;

  const handleDismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, todayKey()); } catch { /* noop */ }
    setHidden(true);
  };

  const handlePrimary = () => {
    const u = urlForAction(moment.primary_action);
    if (u) navigate(u);
  };

  const handleSecondary = (a) => {
    const u = urlForAction(a);
    if (u) navigate(u);
  };

  const handleEnable = async (selectedHour) => {
    if (busy) return;
    setBusy(true);
    try {
      if (isPushSupported()) {
        await enablePush({ hour: selectedHour });
        setNotifyOptIn(true);
        setHourLocal(selectedHour);
        setShowHourPicker(false);
        // Send one demo push right now so the user sees what they signed up for.
        try { await sendTestPush(); } catch { /* non-fatal */ }
        toast.success(
          language === 'es'
            ? `Activado · te recordaremos a las ${formatHour(selectedHour)}`
            : `On — daily reminder at ${formatHour(selectedHour)}`,
        );
      } else if ('Notification' in window) {
        // Fallback: one-shot in-tab Notification only.
        const perm = Notification.permission === 'default'
          ? await Notification.requestPermission()
          : Notification.permission;
        if (perm !== 'granted') throw new Error('denied');
        try { window.localStorage.setItem(NOTIFY_OPT_IN_KEY, '1'); } catch { /* noop */ }
        const n = new Notification(moment.title, {
          body: moment.message,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'lumera-moment-demo',
        });
        n.onclick = () => {
          window.focus();
          const u = urlForAction(moment.primary_action);
          if (u) navigate(u);
          n.close();
        };
        setNotifyOptIn(true);
        setShowHourPicker(false);
        toast.success(
          language === 'es' ? 'Activado · te enviamos uno ahora' : 'On — sent one now',
        );
      } else {
        throw new Error('unsupported');
      }
    } catch (e) {
      const msg = e?.message || '';
      if (msg.includes('denied')) {
        toast.error(language === 'es' ? 'Permiso denegado' : 'Permission denied');
      } else if (msg.includes('unsupported') || msg.includes('not supported')) {
        toast.error(language === 'es' ? 'Tu navegador no soporta notificaciones' : 'Notifications not supported in this browser');
      } else {
        toast.error(language === 'es' ? 'No se pudo activar' : 'Could not turn on notifications');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await disablePush();
      try { window.localStorage.setItem(NOTIFY_OPT_IN_KEY, '0'); } catch { /* noop */ }
      setNotifyOptIn(false);
      setShowHourPicker(false);
      toast.success(language === 'es' ? 'Notificaciones desactivadas' : 'Notifications turned off');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleNotify = () => {
    if (notifyOptIn) {
      handleDisable();
    } else {
      // Open the picker so the user picks a delivery hour first.
      setShowHourPicker(true);
    }
  };

  const Icon = ICON_MAP[moment.icon] || Sparkles;
  const KIND_ACCENT = {
    streak: 'from-orange-500 to-amber-500',
    highlight: 'from-yellow-500 to-amber-500',
    draft: 'from-purple-600 to-fuchsia-600',
    default: 'from-emerald-500 to-teal-500',
  };
  const accent = KIND_ACCENT[moment.kind] || KIND_ACCENT.default;

  return (
    <Card
      className="overflow-hidden border-0 shadow-lg"
      data-testid="daily-moment-card"
    >
      <div className={`relative bg-gradient-to-br ${accent} text-white p-5 sm:p-6`}>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white/90 hover:text-white transition-colors"
          data-testid="daily-moment-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 sm:gap-4 max-w-3xl">
          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-white/20 text-white border-white/30 text-[9px] uppercase tracking-widest">
                {language === 'es' ? 'Tu momento Lumera' : 'Your Lumera moment'}
              </Badge>
            </div>
            <h2
              className="text-lg sm:text-xl font-bold leading-snug"
              data-testid="daily-moment-title"
            >
              {moment.title}
            </h2>
            <p className="text-sm sm:text-base text-white/90 mt-1.5 leading-relaxed">
              {moment.message}
            </p>

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {moment.primary_action && (
                <Button
                  onClick={handlePrimary}
                  className="bg-white text-slate-900 hover:bg-white/95 font-bold shadow-md"
                  data-testid="daily-moment-primary-btn"
                >
                  {moment.primary_action.label}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {(moment.secondary_actions || []).slice(0, 1).map((a, i) => (
                <Button
                  key={`${a.type}-${i}`}
                  onClick={() => handleSecondary(a)}
                  variant="ghost"
                  className="text-white hover:bg-white/15 font-medium"
                  data-testid={`daily-moment-secondary-btn-${i}`}
                >
                  {a.label}
                </Button>
              ))}
              <Button
                onClick={handleToggleNotify}
                size="sm"
                variant="ghost"
                disabled={busy}
                className="ml-auto h-8 px-2 text-white/80 hover:text-white hover:bg-white/15"
                title={
                  notifyOptIn
                    ? (language === 'es' ? `Activado · ${formatHour(hourLocal)}` : `On · ${formatHour(hourLocal)}`)
                    : (language === 'es' ? 'Recibir un recordatorio diario' : 'Get a daily reminder')
                }
                data-testid="daily-moment-notify-btn"
              >
                {notifyOptIn ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
            </div>

            {/* Hour picker: shown only when the user is starting opt-in */}
            {showHourPicker && !notifyOptIn && (
              <div
                className="mt-4 rounded-xl bg-white/15 backdrop-blur-sm p-3 border border-white/20"
                data-testid="daily-moment-hour-picker"
              >
                <div className="text-[11px] uppercase tracking-widest text-white/80 mb-2">
                  {language === 'es' ? 'Recibir mi momento a las' : 'Send my moment at'}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {HOUR_OPTIONS.map((h) => (
                    <button
                      key={h}
                      onClick={() => handleEnable(h)}
                      disabled={busy}
                      className="px-2.5 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white text-xs font-medium transition-colors disabled:opacity-50"
                      data-testid={`daily-moment-hour-${h}`}
                    >
                      {formatHour(h)}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowHourPicker(false)}
                    disabled={busy}
                    className="px-2.5 py-1 rounded-md text-white/70 hover:text-white text-xs"
                    data-testid="daily-moment-hour-cancel"
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DailyMomentCard;
